import moment from 'moment'
import React, { useCallback } from 'react'

import * as Analytics from 'analytics/analytics'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { clearRecentlySavedTripId } from 'storage/recentSavedTrip'
import {
  ACCOMMODATION_TYPE,
  determineItemType,
} from 'tripPlanner/components/TripMaps/MapSearch'
import { useTripId } from 'tripPlanner/contexts/TripContext'
import {
  useCreateTripItem,
  useTrip,
} from 'tripPlanner/hooks/api'
import useBookmarkSnackbarHandlers from 'tripPlanner/hooks/bookmarks/useSavedItemSnackbarHandlers'
import { useTripPermissions } from 'tripPlanner/hooks/useTripPermissions'
import { setCurrentSelectionId } from 'tripPlanner/reducers/actions'
import {
  selectTripPlannerTemplateId,
  selectTripPlannerTemplateItemId,
} from 'tripPlanner/selectors'
import { ExploreItem } from 'tripPlanner/types/common'
import { getMatchingSavedExploreItem } from 'tripPlanner/utils/explore'
import { createExploreItemBookmarkPayload } from 'tripPlanner/utils/itemConversions/bookmarks/exploreItem'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import BaseBookmarkToItinerary from '../../Common/BaseBookmarkToItinerary'

export interface Props {
  exploreItem: ExploreItem
  tripId?: string
  variant: 'chip' | 'button'
  onSave: () => void
  dropdownPlacement?: 'top' | 'bottom'
}

function SearchBookmarkToItineraryButton({
  exploreItem,
  tripId: tripIdProp,
  variant,
  onSave,
  dropdownPlacement,
}: Props) {
  const contextTripId = useTripId()
  const tripId = tripIdProp ?? contextTripId
  const { data: trip } = useTrip({ tripId })
  const { canEdit } = useTripPermissions(tripId)

  const templateId = useAppSelector(selectTripPlannerTemplateId)
  const templateItemId = useAppSelector(selectTripPlannerTemplateItemId)

  const savedItem = trip ?
    getMatchingSavedExploreItem(trip, exploreItem) :
    undefined

  const { showSaveErrorSnackbar } = useBookmarkSnackbarHandlers()

  const dispatch = useAppDispatch()

  const { mutateAsync: createItem, isLoading: isCreating } = useCreateTripItem({
    onSuccess: (createdItem) => {
      onSave()
      dispatch(setCurrentSelectionId(tripItemSelectionId(createdItem.id)))
    },
    onError: (e) => {
      console.error(e)
      showSaveErrorSnackbar(trip!.name)

      // Clear the recently saved trip ID in case it errored because the trip was deleted
      clearRecentlySavedTripId()
    },
  })

  const createTripItemIntoItinerary = useCallback(
    async(date: moment.Moment) => {
      if (!trip) {
        return
      }
      try {
        const type =
          determineItemType(exploreItem.types) === ACCOMMODATION_TYPE ?
            'ACCOMMODATION' :
            'ATTRACTION'

        const payload = createExploreItemBookmarkPayload({
          exploreItem,
          templateId,
          templateItemId,
          startDate: date.format(ISO_DATE_FORMAT),
          type,
        })
        await createItem({
          tripId: trip.id,
          tripItem: payload,
        })
        showSnackbar('Added to itinerary', 'success')
      } catch {
        // Do nothing - failure case is handled by the hook's onError handler
      }
    },
    [trip, exploreItem, templateId, templateItemId, createItem],
  )

  const onSelectDate = useCallback(
    () => {
      Analytics.trackClientEvent({
        action: 'click',
        subject: 'add_to_itinerary_explore_item',
        category: AnalyticsPage.tripPlanner,
        type: 'interaction',
      })
    },
    [],
  )

  if (!canEdit || !tripId) {
    return null
  }

  return <BaseBookmarkToItinerary
    savedItem={savedItem}
    onRequestCreateItem={createTripItemIntoItinerary}
    isCreatingItem={isCreating}
    tripId={tripId}
    variant={variant}
    datesRequiredMessageVariation="exploreItem"
    dropdownPlacement={dropdownPlacement}
    onSelectDate={onSelectDate}
  />
}

export default SearchBookmarkToItineraryButton
